/**
 * Chara Karaka System Data
 * Based on Bhrigu Samhita - 7 variable significators by distance from rashi center (15°)
 * Each planet ranked by proximity to 15° within its rashi
 */

import { JatakaResponse, GrahaPosition } from '../../../../../models/api';

export interface PlanetDisplay {
  name: string;
  short_name: string;
  longitude: number; // Parsed from position string
  is_retrograde: boolean;
  is_lagna?: boolean;
  is_hora?: boolean;
  is_chandra?: boolean;
}

export interface PositionAnalysis {
  positionInRashi: number; // 0°-30° position within rashi
  distanceFromCenter: number; // Distance from 15° center
  isInFirstHalf: boolean; // 0°-15° (true) or 15°-30° (false)
  isMovingToCenter: boolean; // < 15° moving to center, > 15° moving away
}

export interface CharaKarakaAttributes {
  karakaType: string;
  karakaName: string;
  rank: number; // 1-7 (closest to farthest from center)
  planet: PlanetDisplay;
  positionInRashi: number;
  distanceFromCenter: number;
  isInFirstHalf: boolean;
  isMovingToCenter: boolean;
  activeBhavas: number[];
  interpretation: string;
}

export interface CharaKarakaData {
  charaKarakas: CharaKarakaAttributes[];
  atmaKarakaSpecial: {
    planet: PlanetDisplay;
    bhavaFromLagna: number;
    bhavaFromHora: number;
    materialAttachment: 'increasing' | 'decreasing';
  } | null;
}

/**
 * 7 Chara Karaka types according to Bhrigu Samhita
 * Ordered by rank (1 = closest to center, 7 = farthest from center)
 */
export const CHARA_KARAKA_TYPES = [
  'ATMA', 'AMATYA', 'BHRATRI', 'PITRI', 'PUTRA', 'BANDHU', 'DARA'
] as const;

export type CharaKarakaType = typeof CHARA_KARAKA_TYPES[number];

/**
 * Chara Karaka descriptions and meanings
 */
export const KARAKA_DESCRIPTIONS: Record<CharaKarakaType, {
  name: string;
  description: string;
  bhavas: number[];
}> = {
  ATMA: {
    name: 'Atma Karaka',
    description: 'Self, individuality, soul purpose',
    bhavas: [1]
  },
  AMATYA: {
    name: 'Amatya Karaka',
    description: 'Environment, companions, resources',
    bhavas: [2, 12]
  },
  BHRATRI: {
    name: 'Bhratri Karaka',
    description: 'Siblings, courage, communications',
    bhavas: [3, 11]
  },
  PITRI: {
    name: 'Pitri Karaka',
    description: 'Parents, home, career foundation',
    bhavas: [4, 10]
  },
  PUTRA: {
    name: 'Putra Karaka',
    description: 'Children, creativity, teaching',
    bhavas: [5, 9]
  },
  BANDHU: {
    name: 'Bandhu Karaka',
    description: 'Relatives, obstacles, secrets',
    bhavas: [6, 8]
  },
  DARA: {
    name: 'Dara Karaka',
    description: 'Spouse, partnerships, marriage',
    bhavas: [7]
  }
};

/**
 * Parse longitude from position string format "rashi|degrees|longitude|speed"
 * @param position - Position string from API
 * @returns Longitude in degrees
 */
export function parseLongitudeFromPosition(position: string): number {
  if (!position) {
    return 0;
  }

  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }

  return 0;
}

/**
 * Get planet position analysis within its rashi (0°-30°)
 * @param planetLongitude - Global longitude in degrees
 * @returns Position analysis object
 */
export function getPlanetPositionInRashi(planetLongitude: number): PositionAnalysis {
  // Position within current rashi (0°-30°)
  const positionInRashi = planetLongitude % 30;

  // Distance from center of rashi (15°)
  const distanceFromCenter = Math.abs(positionInRashi - 15);

  // First half (0°-15°) or second half (15°-30°)
  const isInFirstHalf = positionInRashi <= 15;

  // Moving toward center (< 15°) or away from center (> 15°)
  const isMovingToCenter = positionInRashi < 15;

  return {
    positionInRashi,
    distanceFromCenter,
    isInFirstHalf,
    isMovingToCenter
  };
}

/**
 * Determine active bhavas for a karaka based on rashi half
 * @param karakaType - Type of karaka
 * @param isInFirstHalf - Whether planet is in first half of rashi
 * @returns Array of active bhava numbers
 */
export function getActiveBhavas(karakaType: CharaKarakaType, isInFirstHalf: boolean): number[] {
  const karaka = KARAKA_DESCRIPTIONS[karakaType];

  // For karakas with single bhava, return it
  if (karaka.bhavas.length === 1) {
    return karaka.bhavas;
  }

  // For karakas with dual bhavas, select based on rashi half
  // First half (0°-15°) = first bhava, Second half (15°-30°) = second bhava
  return isInFirstHalf ? [karaka.bhavas[0]] : [karaka.bhavas[1]];
}

/**
 * Generate interpretation based on karaka type and position
 * @param karaka - Chara karaka attributes
 * @returns Interpretation string
 */
export function getKarakaInterpretation(karaka: CharaKarakaAttributes): string {
  const { karakaType, isInFirstHalf, isMovingToCenter } = karaka;

  const interpretations: Record<CharaKarakaType, { first: string; second: string }> = {
    ATMA: {
      first: isMovingToCenter ? 'Increasing material attachment' : 'Material growth focus',
      second: isMovingToCenter ? 'Approaching spiritual peak' : 'Decreasing material attachment'
    },
    AMATYA: {
      first: 'Environment aids wealth, learning, communication',
      second: 'Environment leads to expenses, detachment, secrets'
    },
    BHRATRI: {
      first: 'Strong with younger siblings, many desires',
      second: 'Patronage from elders, achievements, gains'
    },
    PITRI: {
      first: 'Mother\'s influence predominant in life',
      second: 'Father\'s influence predominant in life'
    },
    PUTRA: {
      first: 'Attached to children, creativity, learning',
      second: 'Role as teacher, guide, spiritual mentor'
    },
    BANDHU: {
      first: 'Relatives help overcome obstacles, enemies',
      second: 'Secrets with relatives, potential losses'
    },
    DARA: {
      first: 'Attachment to spouse, partnership focus',
      second: 'Liberation from attachment, spiritual partnership'
    }
  };

  const interpretation = interpretations[karakaType as CharaKarakaType];
  return isInFirstHalf
    ? interpretation.first
    : interpretation.second;
}

/**
 * Convert API planet to PlanetDisplay format
 * @param planet - GrahaPosition from API response
 * @param currentHoraPlanet - Current hora planet name for highlighting
 * @returns PlanetDisplay object
 */
export function convertToPlanetDisplay(planet: GrahaPosition, currentHoraPlanet?: string): PlanetDisplay {
  // Parse longitude from position string (global degrees)
  const longitude = parseLongitudeFromPosition(planet.position || '');

  // Determine if this is the hora planet
  const isHora = currentHoraPlanet && (
    planet.short_name === currentHoraPlanet ||
    planet.name === currentHoraPlanet
  );

  const result = {
    name: planet.name,
    short_name: planet.short_name,
    longitude: longitude,
    is_retrograde: planet.is_retrograde || false,
    is_lagna: planet.short_name === 'Lg',
    is_hora: isHora || false,
    is_chandra: planet.short_name === 'Ch' || planet.name === 'Chandra'
  };

  return result;
}

/**
 * Calculate all Chara Karakas from API response
 * @param apiResponse - Jataka calculator API response
 * @returns Complete Chara Karaka data
 */
export function calculateCharaKarakas(apiResponse: JatakaResponse): CharaKarakaData {
  if (!apiResponse?.graha_positions?.planets) {
    return { charaKarakas: [], atmaKarakaSpecial: null };
  }

  // Get current hora planet for highlighting (same as D12)
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;
  const horaPlanetName = currentHoraPlanet ? currentHoraPlanet.split(' ')[0] : undefined;

  // Convert API planets to PlanetDisplay format, excluding Rahu, Ketu and Lagna
  const planets = apiResponse.graha_positions.planets
    .filter(planet =>
      planet.short_name !== 'Ra' &&
      planet.short_name !== 'Ke' &&
      planet.short_name !== 'Lg'  // Exclude Rahu, Ketu and Lagna
    )
    .map(planet => convertToPlanetDisplay(planet, horaPlanetName));

  // Calculate position analysis for each planet
  const planetsWithAnalysis = planets.map(planet => {
    const analysis = getPlanetPositionInRashi(planet.longitude);
    return {
      planet,
      ...analysis
    };
  });

  // Sort by distance from center (closest first = Atma Karaka)
  const sortedByDistance = planetsWithAnalysis.sort((a, b) =>
    a.distanceFromCenter - b.distanceFromCenter
  );

  // Assign Chara Karaka types (limit to 7 karakas)
  const charaKarakas: CharaKarakaAttributes[] = sortedByDistance.slice(0, 7).map((item, index) => {
    const karakaType = CHARA_KARAKA_TYPES[index];
    const activeBhavas = getActiveBhavas(karakaType, item.isInFirstHalf);
    const interpretation = getKarakaInterpretation({
      karakaType,
      isInFirstHalf: item.isInFirstHalf,
      isMovingToCenter: item.isMovingToCenter
    } as CharaKarakaAttributes);

    return {
      karakaType,
      karakaName: KARAKA_DESCRIPTIONS[karakaType].name,
      rank: index + 1,
      planet: item.planet,
      positionInRashi: item.positionInRashi,
      distanceFromCenter: item.distanceFromCenter,
      isInFirstHalf: item.isInFirstHalf,
      isMovingToCenter: item.isMovingToCenter,
      activeBhavas,
      interpretation
    };
  });

  // Special Atma Karaka analysis (closest to center)
  const atmaKaraka = charaKarakas[0];
  const atmaKarakaSpecial = atmaKaraka ? {
    planet: atmaKaraka.planet,
    bhavaFromLagna: 1, // TODO: Calculate actual bhava from Lagna
    bhavaFromHora: 1,  // TODO: Calculate actual bhava from Hora
    materialAttachment: (atmaKaraka.isMovingToCenter ? 'increasing' : 'decreasing') as 'increasing' | 'decreasing'
  } : null;

  return {
    charaKarakas,
    atmaKarakaSpecial
  };
}